import { Component } from '@theme/component'
import { CartAddEvent } from '@theme/events'
import { debounce, fetchConfig } from '@theme/utilities'
import { cartPerformance } from '@theme/performance'

/**
 * Handles gift wrap drawer
 *
 * @typedef {Object} GiftWrapDrawerRefs
 * @property {HTMLElement} giftWrapDrawer
 * @property {HTMLElement} giftWrapNoteCharCount
 * @property {HTMLElement} giftWrapAdd
 * @property {HTMLElement[]} giftWrapCancel
 *
 * @extends {Component<GiftWrapDrawerRefs>}
 */
class GiftWrapDrawerComponent extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null

  requiredRefs = [
    'giftWrapDrawer',
    'giftWrapNoteCharCount',
    'giftWrapAdd',
    'giftWrapCancel',
  ]

  connectedCallback() {
    super.connectedCallback()
  }

  close() {
    const { giftWrapDrawer } = this.refs
    giftWrapDrawer.setAttribute('aria-expanded', 'false')
    giftWrapDrawer.classList.remove('is-expanded')

    const giftWrapToggle = document.querySelector('gift-wrap-toggle')
    if (giftWrapToggle) {
      // @ts-ignore
      giftWrapToggle.close()
    }
  }

  // @ts-ignore
  addGiftWrap(event) {
    event.preventDefault()

    const { giftWrapDrawer } = this.refs

    // Send the add to cart information to the cart
    const form = this.querySelector('form')

    if (!form) throw new Error('Product form element missing')

    const formData = new FormData(form)
    const cartItemsComponents = document.querySelectorAll(
      'cart-items-component'
    )
    let cartItemComponentsSectionIds = []
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId)
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','))
    })

    const _this = this
    const fetchCfg = fetchConfig('javascript', { body: formData })
    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        const { key } = response
        if (key) {
          const giftWrapToggle = document.querySelector('gift-wrap-toggle')
          if (giftWrapToggle) {
            // @ts-ignore
            giftWrapToggle.updateLinekeyItem(key)
          }
        }

        this.dispatchEvent(
          new CartAddEvent({}, 'giftWrapForm', {
            source: 'gift-wrap-drawer-component',
            itemCount: 1,
            productId: giftWrapDrawer.dataset.productId,
            sections: response.sections,
          })
        )
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event)
        giftWrapDrawer.classList.add('gift-wrap-in-cart')
        _this.close()
      })
  }

  updateCartAttribute = debounce(async (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return

    const { giftWrapNoteCharCount } = this.refs

    const note = event.target.value
    if (this.#activeFetch) {
      this.#activeFetch.abort()
    }

    const abortController = new AbortController()
    this.#activeFetch = abortController

    giftWrapNoteCharCount.textContent = note.length || 0

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            'Gift note': note,
          },
        }),
      })

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      })
    } catch (error) {
    } finally {
      this.#activeFetch = null
      cartPerformance.measureFromEvent('attribute-update:user-action', event)
    }
  }, 200)
}

if (!customElements.get('gift-wrap-drawer')) {
  customElements.define('gift-wrap-drawer', GiftWrapDrawerComponent)
}

/**
 * Handles gift wrap toggle
 *
 * @typedef {Object} GiftWrapToggleRefs
 * @property {HTMLElement} giftWrapToggle
 * @property {HTMLElement} giftWrapRemove
 *
 * @extends {Component<GiftWrapToggleRefs>}
 */
class GiftWrapToggleComponent extends Component {
  requiredRefs = ['giftWrapToggle', 'giftWrapRemove']

  connectedCallback() {
    super.connectedCallback()
  }

  toggle() {
    const { giftWrapToggle } = this.refs
    const expanded = giftWrapToggle.getAttribute('aria-expanded') === 'true'
    if (expanded) {
      giftWrapToggle.setAttribute('aria-expanded', 'false')
    } else {
      giftWrapToggle.setAttribute('aria-expanded', 'true')
    }

    const cartDrawerDialog = document.querySelector('.cart-drawer__dialog')
    if (cartDrawerDialog) {
      // @ts-ignore
      if (expanded) {
        cartDrawerDialog.classList.remove('is-expanded')
        cartDrawerDialog.setAttribute('aria-expanded', 'false')
      } else {
        cartDrawerDialog.classList.add('is-expanded')
        cartDrawerDialog.setAttribute('aria-expanded', 'true')
      }
    }

    const giftWrapDrawer = document.querySelector('.gift-wrap__drawer')
    if (giftWrapDrawer) {
      if (expanded) {
        giftWrapDrawer.classList.remove('is-expanded')
        giftWrapDrawer.setAttribute('aria-expanded', 'false')
      } else {
        giftWrapDrawer.classList.add('is-expanded')
        giftWrapDrawer.setAttribute('aria-expanded', 'true')
      }
    }
  }

  close() {
    const { giftWrapToggle } = this.refs
    giftWrapToggle.setAttribute('aria-expanded', 'false')

    const cartDrawerDialog = document.querySelector('.cart-drawer__dialog')
    if (cartDrawerDialog) {
      // @ts-ignore
      cartDrawerDialog.classList.remove('is-expanded')
    }
  }

  updateLinekeyItem(linekey = '') {
    const { giftWrapRemove } = this.refs
    giftWrapRemove.dataset.linekeyItem = linekey
  }

  delete() {
    const { giftWrapRemove } = this.refs
    const linekeyItem = giftWrapRemove.dataset.linekeyItem
    if (linekeyItem) {
      const cartItemsComponent = document.querySelector('cart-items-component')
      if (cartItemsComponent) {
        // @ts-ignore
        cartItemsComponent.updateQuantity({
          id: linekeyItem,
          quantity: 0,
          action: 'clear',
        })

        debounce(this.close, 100)
      }
    }
  }
}

if (!customElements.get('gift-wrap-toggle')) {
  customElements.define('gift-wrap-toggle', GiftWrapToggleComponent)
}
